import React, { useEffect } from "react";
import "./home_page.css";
import { useNavigate } from "react-router-dom";
import RevxLogo from "../FillDGF/REVX_LOGO.jpeg";
import { hasAnyPermission } from "../../permissions";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");

const ROLES = {
  SUPER_ADMIN: "SUPER ADMIN",
  ADMIN: "ADMIN",
  OTA_MANAGER: "OTA MANAGER",
  OTA_EXECUTIVE: "OTA EXECUTIVE",
  SALES_EXECUTIVE: "SALES EXECUTIVE",
  ONBOARDING_MANAGER: "ONBOARDING MANAGER",
  ONBOARDING_EXECUTIVE: "ONBOARDING EXECUTIVE",
};

const getAllowedTabs = (role) => {
  switch (role) {
    case ROLES.SUPER_ADMIN:
    case ROLES.ADMIN:
      return [
        "FILL_DGF",
        "PROPERTIES_LIST",
        "SETTINGS",
        "RRC",
        "OTA_DASHBOARD",
        "TOOL_5",
      ];

    case ROLES.OTA_MANAGER:
      return [
        "PROPERTIES_LIST",
        "RRC",
        "OTA_DASHBOARD",
        "TOOL_5",
      ];

    case ROLES.OTA_EXECUTIVE:
    case ROLES.SALES_EXECUTIVE:
  return [
    "PROPERTIES_LIST",
    "RRC",
    "OTA_DASHBOARD",
    "TOOL_5",
  ];

    case ROLES.ONBOARDING_MANAGER:
    case ROLES.ONBOARDING_EXECUTIVE:
      return [
        "FILL_DGF",
        "PROPERTIES_LIST",
      ];

    default:
      return [];
  }
};

function HomePage() {
  const navigate = useNavigate();

  const loggedInUser = localStorage.getItem("loggedInUser");
  const loggedInRole = localStorage.getItem("loggedInRole");

  const allowedTabs = getAllowedTabs(loggedInRole);

  // Granular "SHOW" permissions (assigned via Settings) can additionally
  // unlock a card even for roles that wouldn't normally see it — these
  // are OR'd in on top of the existing role-based list, never removing
  // anything the role already grants.
  if (hasAnyPermission(["show_fill_dgf"]) && !allowedTabs.includes("FILL_DGF")) {
    allowedTabs.push("FILL_DGF");
  }
  if (hasAnyPermission(["show_properties_list"]) && !allowedTabs.includes("PROPERTIES_LIST")) {
    allowedTabs.push("PROPERTIES_LIST");
  }
  if (
    hasAnyPermission(["show_rrc", "show_rrc_revenue_calculator", "show_rrc_property_wise_targets"]) &&
    !allowedTabs.includes("RRC")
  ) {
    allowedTabs.push("RRC");
  }
  if (hasAnyPermission(["show_dashboards"]) && !allowedTabs.includes("OTA_DASHBOARD")) {
    allowedTabs.push("OTA_DASHBOARD");
  }

  const isSuperAdmin = loggedInRole === ROLES.SUPER_ADMIN;
  const isAdmin = loggedInRole === ROLES.ADMIN;
  // const isManagementUser = isSuperAdmin || isAdmin;

  useEffect(() => {
    if (!localStorage.getItem("isAuthenticated")) {
      navigate("/admin");
      return;
    }

    if (!loggedInRole) {
      navigate("/admin");
      return;
    }
  }, [loggedInRole, navigate]);

  const handleLogout = async () => {
  try {
    await fetch(`${API_BASE}/api/logout-log`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: loggedInUser,
        role: loggedInRole,
      }),
    });
  } catch (err) {
    console.error("Logout log failed:", err);
  } finally {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("loggedInRole");
    localStorage.removeItem("role");
    navigate("/admin");
  }
};

  return (
    <div className="home-container">
      <header className="home-header">
        <div
          className="logo-container"
          onClick={() => window.open("https://revxhospitality.com/", "_blank")}
        >
          <img src={RevxLogo} alt="RevX Logo" className="revx-logo" />
        </div>

        <h1 className="home-title">
          {isSuperAdmin
            ? "WELCOME TO SUPER ADMIN DASHBOARD"
            : isAdmin
            ? "WELCOME TO ADMIN DASHBOARD"
            : `WELCOME ${loggedInUser?.toUpperCase() || "USER"}`}
        </h1>

        <div className="right">
          {allowedTabs.includes("SETTINGS") && (
            <button
              className="setting-btn"
              onClick={() => navigate("/SettingsPage")}
              title="Settings"
            >
              ⚙️
            </button>
          )}

          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="home-content">
        {allowedTabs.includes("FILL_DGF") && (
          <div className="home-card">
            <h3>📋 FILL DGF</h3>
            <p>Start filling the property details here and get an overview.</p>
            <button className="home-btn" onClick={() => navigate("/FillDgf")}>
              FILL FORM
            </button>
          </div>
        )}

        {allowedTabs.includes("PROPERTIES_LIST") && (
          <div className="home-card">
            <h3>🏨 PROPERTIES LIST</h3>
            <p>Manage property information and availability.</p>
            <button
              className="home-btn"
              onClick={() => navigate("/PropertiesList")}
            >
              MANAGE
            </button>
          </div>
        )}

        {allowedTabs.includes("RRC") && (
          <div className="home-card">
            <h3>🧮 RRC</h3>
            <p>
              Forecast your property's revenue potential using seasonal trends
              and targets.
            </p>
            <button
              className="home-btn"
              onClick={() => navigate("/HomePageRrc")}
            >
              CALCULATE
            </button>
          </div>
        )}

        {allowedTabs.includes("OTA_DASHBOARD") && (
          <div className="home-card">
            <h3>🥇 DASHBOARDS</h3>
            <p>Track and analyse your OTA performance with actionable insights.</p>
            <button
              className="home-btn"
              onClick={() => navigate("/dashboards")}
            >
              VIEW
            </button>
          </div>
        )}

        {/* "TOOL_5" is the internal key of this card (it used to be a placeholder); it now opens Promotions. */}
        {allowedTabs.includes("TOOL_5") && (
  <div className="home-card">
    <h3>🏷️ PROMOTIONS</h3>
    <p>Create and manage discount rules for each hotel.</p>
    <button className="home-btn" onClick={() => navigate("/promotions")}>
      VIEW
    </button>
  </div>
)}
</div>

<footer className="home-footer">
  © 2026 Developed by <span className="brand">Vaibhav Prajapati</span> at{" "}
  <span style={{ color: "green" }}>RevX Hospitality</span>
</footer>
    </div>
  );
}

export default HomePage;
